import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { useAuth } from '@context/shared/AuthContext';
import { usePageTracking } from '@hooks/client/use-page-tracking';
import { getDefaultLandingPath } from '@config/shared/roles';

// Route modules
import { getAuthRoutes } from './modules/auth.routes';
import { getClientRoutes } from './modules/client.routes';
import { useAdminRoutes } from './modules/admin.routes';
import { LoadingFallback } from './modules/shared';

// Lazy load pages
const NotFoundPage = lazy(() => import('@pages/NotFound'));

// Catch-all for unauthenticated users hitting unknown routes (e.g. /admin/... without features loaded)
// Saves the intended location so login can redirect back
const CatchAllRedirect = () => {
  const location = useLocation();
  return <Navigate to="/auth/login" state={{ from: location }} replace />;
};

const AppRoutes = () => {
  const { features, isAuthenticated, loading, user } = useAuth();
  usePageTracking();

  // Get admin routes with features
  const adminRoutes = useAdminRoutes(features);

  // Show loading screen while auth and features are being initialized
  if (loading) {
    return <LoadingFallback />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Root redirect */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to={getDefaultLandingPath(user?.roleId)} replace />
            ) : (
              <Navigate to="/auth/login" replace />
            )
          }
        />

        {/* Auth routes */}
        {getAuthRoutes()}

        {/* Admin routes - prefixed with /admin */}
        {adminRoutes}

        {/* Client routes - at root level (no /client prefix) */}
        {getClientRoutes()}

        {/* 404 Not Found - redirect to login if not authenticated */}
        <Route
          path="*"
          element={
            isAuthenticated ? (
              <NotFoundPage />
            ) : (
              <CatchAllRedirect />
            )
          }
        />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
